import { useRef, useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { DropdownProps, Position } from './Dropdown.typed';
import Overlay from '../Overlay/Overlay';
import * as Styled from './Dropdown.styled';
import Icon from '../Icon/Icon';
import { useIsomorphicLayoutEffect, useProvidedStateOrCreate } from 'hooks';
import { debounce } from 'lodash';

// TODO: Memoize children for when window resizes
// TODO: Should children have to manually close dropdown on click e.g. Menu?
const Dropdown = (props: DropdownProps) => {
  const {
    children,
    alignX = 'left',
    alignY = 'top',
    isOpen,
    renderAnchor,
    text,
  } = props;

  const toggleState = useCallback(() => {
    !isOpen && !!props.onOpen && props.onOpen();
    isOpen && !!props.onClose && props.onClose();
  }, [isOpen, props]);

  const [combinedOpenState, setCombinedOpenState] = useProvidedStateOrCreate(
    isOpen,
    typeof isOpen !== 'undefined' ? toggleState : undefined,
    false,
  );
  const onOpen = useCallback(
    () => setCombinedOpenState(true),
    [setCombinedOpenState],
  );
  const onClose = useCallback(() => {
    setCombinedOpenState(false);
  }, [setCombinedOpenState]);
  const id = useRef(`dropdown-${uuidv4()}`).current;
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [position, setPosition] = useState<Position>({
    top: 'auto',
    left: 'auto',
    right: 'auto',
    bottom: 'auto',
  });
  const aria = {
    'aria-expanded': combinedOpenState,
    'aria-controls': id,
  };

  const getPositionY = () => {
    if (anchorRef.current) {
      const { top, height } = anchorRef.current.getBoundingClientRect();
      if (alignY === 'top') {
        return top + height + window.scrollY;
      }
      return top + window.scrollY;
    }
    return 0;
  };

  const getPositionX = () => {
    if (anchorRef.current) {
      const { left, right } = anchorRef.current.getBoundingClientRect();
      if (alignX === 'left') {
        return left;
      }
      return window.innerWidth - right;
    }
    return 0;
  };

  const handleWindowEvent = () => {
    const positionX = getPositionX();
    const positionY = getPositionY();
    setPosition({
      top: alignY === 'top' ? positionY : 'auto',
      bottom: alignY === 'bottom' ? positionY : 'auto',
      left: alignX === 'left' ? positionX : 'auto',
      right: alignX === 'right' ? positionX : 'auto',
    });
  };

  // Debounce for performance reasons with scroll events
  const handleScroll = debounce(() => {
    if (combinedOpenState) {
      handleWindowEvent();
    }
  }, 500);

  useIsomorphicLayoutEffect(() => {
    handleWindowEvent();
    window.addEventListener('resize', handleWindowEvent);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('resize', handleWindowEvent);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [combinedOpenState]);

  const handleOnClick = () => {
    combinedOpenState ? onClose() : onOpen();
  };

  const anchor =
    renderAnchor &&
    renderAnchor({
      ref: anchorRef,
      onClick: handleOnClick,
      isOpen: combinedOpenState,
      ...aria,
    });

  return (
    <>
      {/* Only render default anchor if no custom anchor is provided */}
      {anchor ?? (
        <Styled.Button
          type="button"
          isOpen={combinedOpenState}
          onClick={handleOnClick}
          ref={anchorRef}
          {...aria}
        >
          {text}
          <Icon name="ChevronDownOutline" size="xs" />
        </Styled.Button>
      )}
      {combinedOpenState && (
        <Overlay
          shouldTrapFocus={true}
          returnFocusRef={anchorRef}
          onEscape={onClose}
          onClickOutside={onClose}
          ignoreClickRefs={[anchorRef]}
        >
          <Styled.Dropdown role="listbox" id={id} style={{ ...position }}>
            {children({ close: onClose })}
          </Styled.Dropdown>
        </Overlay>
      )}
    </>
  );
};

export default Dropdown;
